import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardLayout from '../Layouts/DashboardLayout';
import TemplatePreviewGrid from './components/TemplatePreviewGrid';
import EnhancedPostPreview from './components/EnhancedPostPreview';
import ActionButtons from './components/ActionButtons';
import SchedulePostModal from '../Dashboard/components/SchedulePostModal';
import { mockRootProps } from './PostGenerationResultsMockData';
import { enhancedTemplateData } from './EnhancedTemplateData';
import logger from '../../utils/logger';

const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '24px 16px 60px',
  [theme.breakpoints.up('sm')]: {
    padding: '32px 20px 70px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '48px 24px 80px'
  }
}));

const PageHeader = styled(Stack)(({ theme }) => ({
  gap: '10px',
  marginBottom: '24px',
  [theme.breakpoints.up('sm')]: {
    gap: '12px',
    marginBottom: '32px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '16px',
    marginBottom: '40px'
  }
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 600,
  color: '#0B91D6',
  [theme.breakpoints.up('sm')]: {
    fontSize: '25px'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const PageDescription = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  color: '#30302E',
  lineHeight: '18px',
  [theme.breakpoints.up('sm')]: {
    fontSize: '14px',
    lineHeight: '20px'
  }
}));

const MainContent = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  gap: '16px',
  [theme.breakpoints.up('sm')]: {
    gap: '20px'
  },
  [theme.breakpoints.up('md')]: {
    gap: '24px'
  }
}));

const PreviewAndSharingContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '20px',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '40px',
    alignItems: 'flex-start',
    flexWrap: 'wrap'
  }
}));

const PostGenerationResults = ({ templatePreviews, generatedPost, sharingOptions }) => {
  const templates = templatePreviews || enhancedTemplateData.templatePreviews;
  const postData = generatedPost || mockRootProps.generatedPost;
  const shareOptions = sharingOptions || mockRootProps.sharingOptions;

  const [selectedTemplateId, setSelectedTemplateId] = useState(
    templates.find(template => template.selected)?.id || templates[0]?.id
  );
  const [showSharingOptions, setShowSharingOptions] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dynamicPostData, setDynamicPostData] = useState(postData);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [storedLocationDetails] = useState(() => {
    try {
      const raw = localStorage.getItem('selectedGMBLocation');
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      logger.error('[PostGenerationResults] Failed to parse stored GMB location:', error);
      return null;
    }
  });

  const storedBusinessAddress = useMemo(() => {
    if (!storedLocationDetails) return '';
    const address = storedLocationDetails.storefrontAddress || storedLocationDetails.address;
    if (!address) return '';
    if (typeof address === 'string') {
      return address;
    }
    const lines = address.addressLines || address.lines;
    const parts = [];
    if (Array.isArray(lines)) {
      parts.push(lines.filter(Boolean).join(', '));
    }
    if (address.locality) parts.push(address.locality);
    if (address.administrativeArea) parts.push(address.administrativeArea);
    if (address.postalCode) parts.push(address.postalCode);
    return parts.filter(Boolean).join(', ');
  }, [storedLocationDetails]);

  const selectedTemplate = templates.find(template => template.id === selectedTemplateId);

  useEffect(() => {
    const reviewData = localStorage.getItem('selectedReviewForPost');
    if (reviewData) {
      try {
        const review = JSON.parse(reviewData);
        setSelectedReview(review);
        const generatedPostContent = {
          title: "Customer",
          subtitle: "Testimonial",
          userProfile: {
            name: review.author,
            title: "Verified Customer",
            avatar: review.avatar || "https://i.pravatar.cc/150?img=5"
          },
          testimonialText: review.text,
          rating: review.rating,
          ctaText: review.rating >= 4 ? "Experience Excellence" : "We Value Your Feedback"
        };
        setDynamicPostData(generatedPostContent);
      } catch (e) {
        logger.error('Error parsing selected review:', e);
        setDynamicPostData(postData);
      }
    } else {
      setDynamicPostData(postData);
    }
  }, [postData]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    logger.log('Template selected:', templateId);
  };

  const handleDownload = () => {
    logger.log('Downloading generated post...');
    const postElement = document.querySelector('.post-preview-container');
    if (!postElement) {
      logger.error('Post preview element not found');
      return;
    }
    import('html2canvas').then(html2canvas => {
      const options = {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        removeContainer: false
      };
      html2canvas.default(postElement, options).then(canvas => {
        const link = document.createElement('a');
        const filename = selectedReview
          ? `visibeen-testimonial-${selectedReview.author.replace(/\s+/g, '-').toLowerCase()}-${new Date().getTime()}.png`
          : `visibeen-post-${new Date().getTime()}.png`;
        link.download = filename;
        link.href = canvas.toDataURL('image/png', 1.0);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }).catch(err => {
        logger.error('Error generating canvas:', err);
      });
    }).catch(err => {
      logger.error('Error loading html2canvas:', err);
    });
  };

  const handleShare = () => {
    logger.log('Opening share options...');
    setShowSharingOptions(prev => !prev);
  };

  const handleSocialShare = (platform) => {
    logger.log('Sharing to platform:', platform);
  };

  const handleOpenScheduleModal = async () => {
    logger.log('Opening schedule post modal...');
    const postElement = document.querySelector('.post-preview-container');
    if (!postElement) {
      logger.error('Post preview element not found');
      return;
    }
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(postElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      const file = new File([blob], `testimonial-post-${Date.now()}.png`, { type: 'image/png' });
      setCapturedImage(file);
      setScheduleModalOpen(true);
      setShowSharingOptions(false);
    } catch (err) {
      logger.error('Error capturing post:', err);
    }
  };

  const handleCloseScheduleModal = () => {
    setScheduleModalOpen(false);
    setCapturedImage(null);
  };

  const handleScheduleSuccess = () => {
    logger.log('Post scheduled successfully');
    setScheduleModalOpen(false);
    setCapturedImage(null);
  };

  return (
    <DashboardLayout>
      <ContentContainer>
        <PageHeader>
          <PageTitle>Create Post</PageTitle>
          <PageDescription>
            {selectedReview
              ? `Creating testimonial post from ${selectedReview.author}'s ${selectedReview.rating}-star review: "${selectedReview.text.substring(0, 80)}${selectedReview.text.length > 80 ? '...' : ''}"`
              : 'Choose a template and customize your testimonial post design.'
            }
          </PageDescription>
        </PageHeader>

        <MainContent>
          <TemplatePreviewGrid
            templates={templates}
            selectedTemplateId={selectedTemplateId}
            onTemplateSelect={handleTemplateSelect}
          />

          <PreviewAndSharingContainer>
            <EnhancedPostPreview
              generatedPost={dynamicPostData}
              selectedTemplate={selectedTemplate}
            />
          </PreviewAndSharingContainer>

          <ActionButtons
            onDownload={handleDownload}
            onShare={handleShare}
            showSharingOptions={showSharingOptions}
            sharingOptions={shareOptions}
            onSocialShare={handleSocialShare}
            onOpenScheduleModal={handleOpenScheduleModal}
          />
        </MainContent>
      </ContentContainer>

      <SchedulePostModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        onSuccess={handleScheduleSuccess}
        preloadedImage={capturedImage}
        initialContent={dynamicPostData?.testimonialText || ''}
        accountId={localStorage.getItem('selectedGMBAccount') ? JSON.parse(localStorage.getItem('selectedGMBAccount')).name?.split('/').pop() : null}
        locationId={localStorage.getItem('selectedGMBLocation') ? JSON.parse(localStorage.getItem('selectedGMBLocation')).name?.split('/').pop() : null}
        businessName={storedLocationDetails?.title || storedLocationDetails?.locationName || dynamicPostData?.businessName || ''}
        businessCategory={storedLocationDetails?.categories?.primaryCategory?.displayName || storedLocationDetails?.category || ''}
        businessAddress={storedBusinessAddress}
        businessKeywords={storedLocationDetails?.keywords || []}
        businessCoordinates={storedLocationDetails?.latlng || null}
      />
    </DashboardLayout>
  );
};

export default PostGenerationResults;
